import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Upload, CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useCart } from "../hooks/useCart";
import { API } from "../config/constants";
import SEO from "../components/SEO";

export default function MerchPage() {
  const { addToCart } = useCart();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedTentConfig, setSelectedTentConfig] = useState({});
  const [selectedTentSize, setSelectedTentSize] = useState({});
  const [selectedPulleyBag, setSelectedPulleyBag] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [standardProducts, setStandardProducts] = useState([]);
  const [tentProduct, setTentProduct] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/merch-products`);
        const all = res.data;
        setStandardProducts(all.filter(p => p.type !== "tent"));
        setTentProduct(all.find(p => p.type === "tent") || null);
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        setPageLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const getOption = (productId, optionType) => selectedOptions[`${productId}-${optionType}`];
  const setOption = (productId, optionType, value) => setSelectedOptions(prev => ({ ...prev, [`${productId}-${optionType}`]: value }));

  const handleAddToCart = (product) => {
    const sizeOptions = product.sizeOptions || [];
    const colorOptions = product.colorOptions || [];
    const size = product.hasSize ? (getOption(product.id, 'size') || sizeOptions[0]) : null;
    const color = product.hasColor ? (getOption(product.id, 'color') || colorOptions[0]) : (product.color || null);
    let itemName = product.name;
    let itemId = product.id;
    if (size) { itemName += ` (${size})`; itemId += `-${size}`; }
    if (color) { itemName += ` - ${color}`; itemId += `-${color}`; }
    addToCart({ id: itemId.replace(/\s/g, '-'), name: itemName, price: product.price, quantity: 1, image_url: product.image_url, type: product.type, shipping: product.shipping });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowed.includes(file.type)) { setUploadError("Only JPEG, PNG, and PDF files are accepted."); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError("File too large (max 10MB)."); return; }
    setUploadError("");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      setUploadedFile({ id: res.data.id, name: file.name });
    } catch { setUploadError("Upload failed. Please try again."); }
    finally { setUploading(false); }
  };

  const handleAddTentToCart = () => {
    if (!tentProduct) return;
    const configs = tentProduct.tentConfigs || [];
    const sizes = tentProduct.tentSizes || [];
    const pricing = tentProduct.tentPricing || {};
    const bagPricing = tentProduct.pulleyBagPricing || {};
    const config = selectedTentConfig[tentProduct.id] || configs[0];
    const size = selectedTentSize[tentProduct.id] || sizes[0];
    let price = pricing[config]?.[size] || 0;
    let itemName = `Custom Event Tent - ${config} (${size})`;
    if (selectedPulleyBag) {
      price += bagPricing[size] || 0;
      itemName += ` + Pulley Bag`;
    }
    addToCart({ id: `${tentProduct.id}-${config}-${size}${selectedPulleyBag ? '-bag' : ''}`.replace(/\s/g, '-'), name: itemName, price, quantity: 1, image_url: (tentProduct.configImages || {})[config] || "", type: 'tent', shipping: tentProduct.shipping || 250, file_id: uploadedFile?.id || null });
  };

  const getCurrentTentPrice = () => {
    if (!tentProduct) return 0;
    const config = selectedTentConfig[tentProduct.id] || (tentProduct.tentConfigs || [])[0];
    const size = selectedTentSize[tentProduct.id] || (tentProduct.tentSizes || [])[0];
    let price = (tentProduct.tentPricing || {})[config]?.[size] || 0;
    if (selectedPulleyBag) price += (tentProduct.pulleyBagPricing || {})[size] || 0;
    return price;
  };

  const getCurrentTentSize = () => {
    if (!tentProduct) return "10x10";
    return selectedTentSize[tentProduct.id] || (tentProduct.tentSizes || [])[0];
  };

  const getTentImage = (config) => {
    if (!tentProduct) return "";
    return (tentProduct.configImages || {})[config] || Object.values(tentProduct.configImages || {})[0] || "";
  };

  if (pageLoading) {
    return (
      <div data-testid="merch-page" className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D90429]" size={40} />
      </div>
    );
  }

  return (
    <div data-testid="merch-page" className="pt-20">
      <SEO title="Merch Store" description="Official Lane4 apparel, custom event tents, hats, tumblers and more. Shop the best track & field gear." />
      <section className="py-16 bg-[#0F2137]">
        <div className="container-custom">
          <span className="section-kicker">Official Gear</span>
          <h1 className="text-5xl font-bold text-white">Lane4 Merch</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {standardProducts.map((product) => (
              <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-xl overflow-hidden shadow-lg card-hover group" data-testid={`product-card-${product.id}`}>
                <div className="relative overflow-hidden bg-gray-50">
                  <img src={product.image_url} alt={product.name} className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <span className="text-xs text-[#D90429] uppercase tracking-wider">{product.category}</span>
                  <h3 className="text-lg font-bold text-[#0F2137] mt-1 mb-2">{product.name}</h3>
                  <p className="text-[#495057] text-sm mb-4 line-clamp-2">{product.description}</p>
                  {product.hasSize && product.sizeOptions && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-[#0F2137] mb-1">Size</label>
                      <select value={getOption(product.id, 'size') || product.sizeOptions[0]} onChange={(e) => setOption(product.id, 'size', e.target.value)} className="w-full px-3 py-2 text-sm border border-[#E9ECEF] rounded-lg bg-white text-[#0F2137] focus:ring-2 focus:ring-[#D90429] focus:border-transparent">
                        {product.sizeOptions.map((size) => (<option key={size} value={size}>{size}</option>))}
                      </select>
                    </div>
                  )}
                  {product.hasColor && product.colorOptions && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-[#0F2137] mb-1">Color</label>
                      <select value={getOption(product.id, 'color') || product.colorOptions[0]} onChange={(e) => setOption(product.id, 'color', e.target.value)} className="w-full px-3 py-2 text-sm border border-[#E9ECEF] rounded-lg bg-white text-[#0F2137] focus:ring-2 focus:ring-[#D90429] focus:border-transparent">
                        {product.colorOptions.map((color) => (<option key={color} value={color}>{color}</option>))}
                      </select>
                    </div>
                  )}
                  {product.color && !product.hasColor && (
                    <div className="mb-3"><span className="text-xs font-medium text-[#495057]">Color: {product.color}</span></div>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <div>
                      <span className="text-xl font-bold text-[#D90429]">${product.price.toFixed(2)}</span>
                      <span className="text-xs text-[#495057] block">+${product.shipping} shipping</span>
                    </div>
                    <button onClick={() => handleAddToCart(product)} data-testid={`add-to-cart-${product.id}`} className="bg-[#D90429] text-white font-semibold py-2 px-4 rounded-lg hover:bg-[#B00322] transition-colors text-sm">Add to Cart</button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {tentProduct && (
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-12 bg-white rounded-xl overflow-hidden shadow-lg card-hover" data-testid="product-card-tent">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative overflow-hidden bg-gray-50">
                  <img src={getTentImage(selectedTentConfig[tentProduct.id] || (tentProduct.tentConfigs || [])[0])} alt={tentProduct.name} className="w-full h-72 md:h-80 object-contain bg-white p-4" />
                </div>
                <div className="p-8">
                  <span className="text-xs text-[#D90429] uppercase tracking-wider">{tentProduct.category}</span>
                  <h3 className="text-2xl font-bold text-[#0F2137] mt-1 mb-2">{tentProduct.name}</h3>
                  <p className="text-[#495057] mb-6">{tentProduct.description}</p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#0F2137] mb-2">Configuration</label>
                    <select value={selectedTentConfig[tentProduct.id] || (tentProduct.tentConfigs || [])[0]} onChange={(e) => setSelectedTentConfig({ ...selectedTentConfig, [tentProduct.id]: e.target.value })} className="w-full px-4 py-3 border border-[#E9ECEF] rounded-lg bg-white text-[#0F2137] focus:ring-2 focus:ring-[#D90429] focus:border-transparent" data-testid="tent-config-select">
                      {(tentProduct.tentConfigs || []).map((config) => (<option key={config} value={config}>{config}</option>))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#0F2137] mb-2">Size</label>
                    <select value={selectedTentSize[tentProduct.id] || (tentProduct.tentSizes || [])[0]} onChange={(e) => setSelectedTentSize({ ...selectedTentSize, [tentProduct.id]: e.target.value })} className="w-full px-4 py-3 border border-[#E9ECEF] rounded-lg bg-white text-[#0F2137] focus:ring-2 focus:ring-[#D90429] focus:border-transparent" data-testid="tent-size-select">
                      {(tentProduct.tentSizes || []).map((size) => (<option key={size} value={size}>{size}</option>))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#0F2137] mb-2">Add-Ons</label>
                    <div className="flex items-center gap-3 px-4 py-3 border border-[#E9ECEF] rounded-lg bg-white">
                      <input type="checkbox" id="pulley-bag" checked={selectedPulleyBag} onChange={(e) => setSelectedPulleyBag(e.target.checked)} className="w-4 h-4 text-[#D90429] rounded focus:ring-[#D90429]" data-testid="pulley-bag-checkbox" />
                      <label htmlFor="pulley-bag" className="text-sm text-[#0F2137] cursor-pointer flex-1">
                        Tent Pulley Bag <span className="font-semibold text-[#D90429]">(+${((tentProduct.pulleyBagPricing || {})[getCurrentTentSize()] || 0).toFixed(2)})</span>
                      </label>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#0F2137] mb-2">Upload Logo/Design <span className="text-xs text-[#495057] font-normal">(JPEG, PNG, or PDF)</span></label>
                    <div className="relative">
                      <input type="file" accept=".jpeg,.jpg,.png,.pdf" onChange={handleFileUpload} className="hidden" id="tent-file-upload" data-testid="tent-file-upload" />
                      <label htmlFor="tent-file-upload" className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-[#E9ECEF] rounded-lg cursor-pointer hover:border-[#D90429] transition-colors bg-[#F8F9FA]">
                        {uploading ? (
                          <div className="flex items-center gap-2 text-sm text-[#495057]"><div className="animate-spin w-5 h-5 border-2 border-[#D90429] border-t-transparent rounded-full" /> Uploading...</div>
                        ) : uploadedFile ? (
                          <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle size={18} /> {uploadedFile.name}</div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-[#495057]"><Upload size={18} /> Choose file...</div>
                        )}
                      </label>
                      {uploadError && <p className="text-red-500 text-xs mt-1" data-testid="upload-error">{uploadError}</p>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-3xl font-bold text-[#D90429]" data-testid="tent-total-price">${getCurrentTentPrice().toFixed(2)}</span>
                      <span className="text-sm text-[#495057] block">+$250 flat rate shipping</span>
                    </div>
                    <button onClick={handleAddTentToCart} data-testid="add-to-cart-tent" className="btn-primary py-3 px-6">Add to Cart</button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
